import { Badge } from "@/components/ui/badge";
import { parseRoteiroPayload } from "@/features/roteiros/parseRoteiro";

function ScriptParagraphs({ text }: { text: string }) {
  // Split by explicit newlines first; fall back to sentence boundaries
  const paragraphs = text.includes("\n")
    ? text.split(/\n+/).map((s) => s.trim()).filter(Boolean)
    : text
        .split(/(?<=[.!?…])\s+(?=[\p{Lu}\p{Emoji_Presentation}"«"'])/u)
        .map((s) => s.trim())
        .filter(Boolean);

  if (paragraphs.length <= 1) {
    return (
      <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{text}</p>
    );
  }

  return (
    <div className="space-y-2">
      {paragraphs.map((p, i) => (
        <p key={i} className="text-sm text-foreground leading-relaxed">
          {p}
        </p>
      ))}
    </div>
  );
}

export function RoteiroScriptView({ script }: { script: string }) {
  const parsed = parseRoteiroPayload(script);

  if (!parsed) {
    return (
      <pre className="text-sm text-foreground whitespace-pre-wrap font-mono leading-relaxed bg-secondary/30 rounded-lg p-4">
        {script}
      </pre>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {parsed.typeLabel ? <Badge variant="secondary">{parsed.typeLabel}</Badge> : null}
        {parsed.objetivoLabel ? <Badge variant="outline">{parsed.objetivoLabel}</Badge> : null}
        {typeof parsed.duracaoEstimadaSegundos === "number" ? (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-secondary/50 text-muted-foreground">
            ⏱ {parsed.duracaoEstimadaSegundos}s
          </span>
        ) : null}
      </div>

      {parsed.estruturaSlides?.length ? (
        <section className="space-y-3">
          {parsed.estruturaSlides.map((slide, index) => (
            <div
              key={`${slide.slide}-${slide.content}`}
              className="rounded-xl border border-border/50 bg-secondary/20 p-4 space-y-3"
            >
              {slide.sceneDirection ? (
                <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-border/40">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {slide.sceneDirection}
                  </p>
                  <div className="flex items-center gap-1.5">
                    {slide.durationLabel ? (
                      <Badge variant="outline" className="text-[10px]">{slide.durationLabel}</Badge>
                    ) : null}
                    {slide.highlight ? (
                      <Badge variant="secondary" className="text-[10px]">{slide.highlight}</Badge>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                    {parsed.typeValue === "Stories" ? "Story" : "Cena"} {slide.slide ?? index + 1}
                  </span>
                  {slide.durationLabel ? (
                    <Badge variant="outline" className="text-[11px]">{slide.durationLabel}</Badge>
                  ) : null}
                  {slide.highlight ? (
                    <Badge variant="secondary" className="text-[11px]">{slide.highlight}</Badge>
                  ) : null}
                </div>
              )}
              <ScriptParagraphs text={slide.content} />
              {slide.cta ? (
                <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-green-400 mb-1">
                    CTA
                  </p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{slide.cta}</p>
                </div>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}

      {parsed.estruturaTexto && !parsed.plainSections?.length ? (
        <section className="bg-secondary/30 border border-border/50 rounded-lg p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Roteiro
          </h4>
          <ScriptParagraphs text={parsed.estruturaTexto} />
        </section>
      ) : null}

      {parsed.plainSections?.length ? (
        <section className="grid gap-3">
          {parsed.plainSections.map((section, index) => (
            <div
              key={`${section.label}-${index}`}
              className="rounded-xl border border-border/50 bg-card/60 p-4 space-y-2"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {section.label}
              </p>
              <ScriptParagraphs text={section.body} />
            </div>
          ))}
        </section>
      ) : null}

      {parsed.hook && (
        <section className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">Hook</h4>
          <ScriptParagraphs text={parsed.hook} />
        </section>
      )}
      {parsed.desenvolvimento && (
        <section className="bg-secondary/30 border border-border/50 rounded-lg p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Desenvolvimento
          </h4>
          <ScriptParagraphs text={parsed.desenvolvimento} />
        </section>
      )}
      {parsed.cta && (
        <section className="bg-green-500/5 border-l-4 border-green-500 rounded-r-lg p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-green-400 mb-2">CTA</h4>
          <ScriptParagraphs text={parsed.cta} />
        </section>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {parsed.hashtags.map((h) => (
          <span
            key={h}
            className="inline-flex items-center text-xs px-2 py-1 rounded-md bg-primary/10 text-primary border border-primary/20"
          >
            {h.startsWith("#") ? h : `#${h}`}
          </span>
        ))}
      </div>
    </div>
  );
}
